import { StatusCountsEntry, isProgressRow } from "../../store/statusCountsSlice";
import {
  MinActiveRowsForRag,
  RagBand,
  completedPercent,
  formatAgo,
  formatClock,
  minutesUntilColour,
  noIconReason,
  ragBand,
} from "./ragBand";

export const ProgressUnavailable = "Progress unavailable.";

export type StatusProgress =
  | { kind: "unavailable" }
  | {
      kind: "counts";
      band: RagBand;
      headline: string;
      percent: number;
      breakdown: string;
      excluded: string | null;
      timeline: string;
      reason: string | null;
      caveat: string | null;
    };

/**
 * Lead-in of the zero-completed reason. Completed rows older than the retention window are
 * deleted, so with a known window it says "in the last N days" rather than "ever".
 */
export const noCompletionsText = (retentionDays: number | null, subject: "this key" | "the key"): string =>
  retentionDays != null
    ? `No completions on ${subject} in the last ${retentionDays} days`
    : `Nothing on ${subject} has completed yet`;

const reasonText = (entry: Extract<StatusCountsEntry, { state: "loaded" }>, retentionDays: number | null): string | null => {
  switch (noIconReason(entry.data, entry.asOf)) {
    case "nothingCompleted":
      return `${noCompletionsText(retentionDays, "this key")}. The data has probably not been published.`;
    case "notEnoughRows":
      return entry.data.active === 1
        ? `Only 1 row shares this key, fewer than the ${MinActiveRowsForRag} needed to compare.`
        : `Only ${entry.data.active} rows share this key, fewer than the ${MinActiveRowsForRag} needed to compare.`;
    case "firstCompletionUnknown":
      return "Progress unavailable: first completion time not known.";
    default:
      return null;
  }
};

/** Facts for the status-cell tooltip: counts, first-resolved time, as-of time and the retention caveat. */
export function describeStatusProgress(entry: StatusCountsEntry | undefined, retentionDays: number | null): StatusProgress {
  if (!entry || entry.state !== "loaded") {
    return { kind: "unavailable" };
  }

  const { data, asOf } = entry;
  const band = ragBand(data, asOf);
  const percent = completedPercent(data);

  const breakdown = [
    data.counts.Waiting ? `${data.counts.Waiting} waiting` : null,
    data.counts.Notified ? `${data.counts.Notified} notified` : null,
    data.counts.Failed ? `${data.counts.Failed} failed` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const excludedParts = [
    data.counts.Terminated ? `+${data.counts.Terminated} terminated` : null,
    data.counts.Abandoned ? `+${data.counts.Abandoned} abandoned` : null,
  ].filter(Boolean);

  const firstAgeMs = data.firstCompletedAt ? Date.parse(asOf) - Date.parse(data.firstCompletedAt) : NaN;
  const firstResolved = Number.isNaN(firstAgeMs) ? null : `First resolved ${formatAgo(firstAgeMs)} ago`;

  const timeline =
    band === "resolving" && firstResolved
      ? `${firstResolved} — status colour in ${minutesUntilColour(data, asOf)}m · As of ${formatClock(asOf)}`
      : `${firstResolved ? `${firstResolved} · ` : ""}As of ${formatClock(asOf)}`;

  return {
    kind: "counts",
    band,
    headline: `This dependency key: ${data.counts.Completed} of ${data.active} completed (${percent}%)`,
    percent,
    breakdown,
    excluded: excludedParts.length ? `(${excludedParts.join(", ")}, not counted)` : null,
    timeline,
    reason: band === "none" ? reasonText(entry, retentionDays) : null,
    caveat:
      retentionDays != null
        ? `Completed rows are kept for at least ${retentionDays} days; older completions may not be counted.`
        : null,
  };
}

type Row = { id?: string; status?: unknown } | undefined;

/**
 * Plain-text tooltip for a status cell (the tooltipValueGetter). Undefined, so no tooltip, for
 * group rows and for Completed / Terminated / Abandoned rows.
 */
export function statusTooltipText(row: Row, entry: StatusCountsEntry | undefined, retentionDays: number | null): string | undefined {
  if (!isProgressRow(row)) {
    return undefined;
  }

  const progress = describeStatusProgress(entry, retentionDays);

  if (progress.kind === "unavailable") {
    return ProgressUnavailable;
  }

  return [
    progress.headline,
    [progress.breakdown, progress.excluded].filter(Boolean).join(" "),
    progress.timeline,
    progress.reason ? `Why no icon: ${progress.reason}` : null,
    progress.caveat,
  ]
    .filter(Boolean)
    .join("\n");
}

/** Accessible name of the cell's marker, e.g. "Key progress: green, 47 of 50 completed". */
export function markerLabel(entry: StatusCountsEntry | undefined): string | undefined {
  if (!entry || entry.state === "error") {
    return undefined;
  }

  if (entry.state === "loading") {
    return "Key progress: loading";
  }

  const band = ragBand(entry.data, entry.asOf);
  const completed = `${entry.data.counts.Completed} of ${entry.data.active} completed`;

  switch (band) {
    case "none":
      return undefined;
    case "resolving": {
      const minutes = minutesUntilColour(entry.data, entry.asOf);
      return `Key progress: resolving, ${completed}, status colour in ${minutes} minute${minutes === 1 ? "" : "s"}`;
    }
    default:
      return `Key progress: ${band}, ${completed}`;
  }
}
